import React from 'react';
import { Monitor, Apple, Terminal, CheckCircle, Clock, Star, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';

const OperatingSystemsPage = ({ setCurrentSection, onSelectGuide, guides }) => {
  // Function to count procedures in a guide by analyzing internal links
  const countProceduresInGuide = (guideId) => {
    const guide = guides.find(g => g.id === guideId);
    if (!guide || !guide.content || !guide.content.sections) return 0;
    
    let linkCount = 0;
    guide.content.sections.forEach(section => {
      if (section.content) {
        // Count links in format [text](link-id)
        const links = section.content.match(/\[([^\]]+)\]\(([^)]+)\)/g);
        if (links) {
          linkCount += links.length;
        }
      }
    });
    
    return linkCount;
  };

  // Organiser les guides par systèmes d'exploitation
  const osCategories = {
    windows: {
      name: 'Windows 11',
      icon: Monitor,
      description: 'Configuration et optimisation pour la productivité',
      color: 'from-blue-500 to-blue-600',
      guideId: 'windows-11-optimization'
    },
    macos: {
      name: 'macOS',
      icon: Apple,
      description: 'Productivité et sécurité sur Mac',
      color: 'from-gray-500 to-gray-600',
      guideId: 'macos-productivity-security'
    },
    linux: {
      name: 'Linux',
      icon: Terminal,
      description: 'Administration et ligne de commande',
      color: 'from-green-500 to-green-600',
      guideId: 'linux-administration-productivity'
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Full Width Banner */}
      <section className="relative overflow-hidden bg-gradient-to-r from-slate-600 via-gray-700 to-blue-700">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center">
            <div className="bg-white bg-opacity-20 p-4 rounded-2xl mr-6 backdrop-blur-sm group-hover:scale-110 transition-transform duration-300">
              <Monitor className="h-16 w-16 text-white" />
            </div>
            <div>
              <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-4">Systèmes d'exploitation</h1>
              <p className="text-slate-100 text-xl md:text-2xl max-w-4xl leading-relaxed">
                Maîtrisez Windows 11, macOS et Linux • Configuration • Optimisation • Sécurité • Productivité
              </p>
            </div>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-blue-300 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-slate-300 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-gray-300 rounded-full opacity-25 animate-ping"></div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <Breadcrumb 
          items={[{ id: 'operating-systems', label: 'Systèmes d\'exploitation' }]}
        />

        {/* Section Guides par OS */}
        <section className="mb-16 bg-gradient-to-r from-slate-50 to-blue-50 rounded-3xl p-8 shadow-lg">
          <div className="flex items-center mb-8">
            <Star className="h-8 w-8 text-slate-600 mr-3" />
            <h2 className="text-3xl font-bold text-gray-900">Guides par Système d'exploitation</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(osCategories).map(([key, os]) => {
              const IconComponent = os.icon;
              const osGuide = guides.find(guide => guide.id === os.guideId);
              const hasGuides = osGuide ? true : false;
              const procedureCount = hasGuides ? countProceduresInGuide(os.guideId) : 0;
              
              return (
                <Card 
                  key={key} 
                  className="group cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-2 border-0 overflow-hidden bg-white h-full flex flex-col"
                  onClick={() => hasGuides && onSelectGuide(osGuide)}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${os.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none`}></div>
                  
                  <CardHeader className="text-center pb-4 relative z-10">
                    <div className={`mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br ${os.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <IconComponent className="h-10 w-10 text-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                      {os.name}
                    </CardTitle>
                    <div className="flex items-center justify-center text-gray-600 text-sm mb-3">
                      {hasGuides ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1 text-green-500" />
                          {procedureCount > 0 ? `${procedureCount} procédure${procedureCount > 1 ? 's' : ''}` : '1 guide'}
                        </>
                      ) : (
                        <>
                          <Clock className="h-4 w-4 mr-1 text-orange-500" />
                          Bientôt disponible
                        </>
                      )}
                    </div>
                  </CardHeader>
                  
                  <CardContent className="text-center pt-0 relative z-10 flex flex-col flex-grow">
                    <CardDescription className="text-gray-600 mb-6 leading-relaxed flex-grow">
                      {os.description}
                    </CardDescription>
                    
                    <Button 
                      variant="ghost"
                      disabled={!hasGuides}
                      className={`w-full font-bold transition-all duration-300 rounded-xl py-2 mt-auto ${
                        hasGuides 
                          ? 'group-hover:bg-gradient-to-r group-hover:from-slate-500 group-hover:to-blue-600 group-hover:text-white' 
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                    >
                      {hasGuides ? 'Consulter le guide' : 'Prochainement'}
                      {hasGuides && <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />}
                    </Button>
                  </CardContent>
                  
                  {hasGuides && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-1000 pointer-events-none"></div>
                  )}
                </Card>
              );
            })}
          </div>
        </section>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default OperatingSystemsPage;